import React, { useState, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import SEOHead from '../components/SEOHead';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

// Helper function to create red marker icon
// Using encodeURIComponent instead of btoa for better production compatibility
// This fixes issues where btoa() may not work correctly in production builds
const createRedIcon = () => {
  try {
    const svgString = `<svg xmlns="http://www.w3.org/2000/svg" width="25" height="41" viewBox="0 0 25 41"><path fill="#dc2626" d="M12.5 0C5.6 0 0 5.6 0 12.5c0 12.5 12.5 28.5 12.5 28.5S25 25 25 12.5C25 5.6 19.4 0 12.5 0zm0 17c-2.5 0-4.5-2-4.5-4.5s2-4.5 4.5-4.5 4.5 2 4.5 4.5-2 4.5-4.5 4.5z"/></svg>`;
    // Use encodeURIComponent for better production compatibility
    // This is more reliable than btoa() in production builds
    const encodedSvg = encodeURIComponent(svgString);
    
    return new L.Icon({
      iconUrl: `data:image/svg+xml;charset=utf-8,${encodedSvg}`,
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [0, -41],
    });
  } catch (error) {
    // Fallback: create a simple div icon if encoding fails
    // This should not happen, but provides a safety net
    return new L.DivIcon({
      html: '<div style="background-color: #dc2626; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white;"></div>',
      iconSize: [12, 12],
      iconAnchor: [6, 6],
      popupAnchor: [0, -6],
    });
  }
};

const GlobalMap = () => {
  const [cityStats, setCityStats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Create icon using useMemo to ensure it's created after component mounts
  // This fixes production build issues where module-level icon creation can fail
  const redIcon = useMemo(() => createRedIcon(), []);

  useEffect(() => {
    fetchCityStats();
  }, []);

  const fetchCityStats = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API}/city-stats`);
      setCityStats(response.data || []);
    } catch (err) {
      setError('Failed to load city statistics');
      console.error('Error fetching city stats:', err);
    } finally {
      setLoading(false);
    }
  };

  // Filter out cities with invalid coordinates
  const validCityStats = cityStats.filter(
    (city) => city.lat && city.lng && city.lat !== '0.0' && city.lng !== '0.0'
  );

  // Sort cities by donor count and get top 10
  const topCities = [...cityStats]
    .sort((a, b) => (b.donor_count || 0) - (a.donor_count || 0))
    .slice(0, 10);

  // Calculate summary stats
  const totalDonors = cityStats.reduce((sum, city) => sum + (city.donor_count || 0), 0);
  const totalCities = cityStats.length;
  const totalRequests = cityStats.reduce((sum, city) => sum + (city.requests_count || 0), 0);

  // Default center (world view)
  const defaultCenter = [20, 0];
  const defaultZoom = 2;

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://donorshouse.com';

  const breadcrumbs = [
    { name: 'Home', url: siteUrl },
    { name: 'Global Map', url: `${siteUrl}/global-map` }
  ];

  return (
    <>
      <SEOHead
        title="Global Donor Map | Interactive Worldwide Donation Statistics | DonorsHouse"
        description="Explore our interactive global map showing donor locations, active blood requests, and donation statistics worldwide. See real-time data on donation activity across cities and countries."
        keywords="donor map, global donors, donation locations, worldwide donors, donation statistics, interactive map, donation activity, city statistics"
        breadcrumbs={breadcrumbs}
      />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Global Donor Map</h1>

        {/* Summary Stats Block */}
        {!loading && !error && cityStats.length > 0 && (
          <div data-testid="summary-stats" className="mb-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
              <Card className="text-center border-none shadow-md">
                <CardContent className="pt-6">
                  <div className="text-3xl lg:text-4xl font-bold text-red-700 mb-2">
                    {totalDonors.toLocaleString()}
                  </div>
                  <p className="text-gray-700 font-medium">Total Donors</p>
                </CardContent>
              </Card>
              <Card className="text-center border-none shadow-md">
                <CardContent className="pt-6">
                  <div className="text-3xl lg:text-4xl font-bold text-red-700 mb-2">
                    {totalCities}
                  </div>
                  <p className="text-gray-700 font-medium">Active Cities</p>
                </CardContent>
              </Card>
              <Card className="text-center border-none shadow-md">
                <CardContent className="pt-6">
                  <div className="text-3xl lg:text-4xl font-bold text-red-700 mb-2">
                    {totalRequests.toLocaleString()}
                  </div>
                  <p className="text-gray-700 font-medium">Active Requests</p>
                </CardContent>
              </Card>
              <Card className="text-center border-none shadow-md">
                <CardContent className="pt-6">
                  <div className="text-3xl lg:text-4xl font-bold text-red-700 mb-2">
                    {validCityStats.length}
                  </div>
                  <p className="text-gray-700 font-medium">Cities on Map</p>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {loading && (
          <div className="text-center py-12">
            <p className="text-gray-600">Loading map data...</p>
          </div>
        )}

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
            <p>{error}</p>
          </div>
        )}

        {!loading && !error && (
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div style={{ height: '600px', width: '100%' }}>
              <MapContainer
                center={defaultCenter}
                zoom={defaultZoom}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {validCityStats.map((city) => (
                  <Marker
                    key={city.id || `${city.city}-${city.country}`}
                    position={[parseFloat(city.lat), parseFloat(city.lng)]}
                    icon={redIcon}
                    data-icon-color="red"
                  >
                    <Popup>
                      <div className="p-2">
                        <h3 className="font-semibold text-gray-900 mb-1">
                          {city.city}, {city.country}
                        </h3>
                        <p className="text-sm text-gray-700">
                          <strong>Donors:</strong> {city.donor_count || 0}
                        </p>
                        {city.requests_count > 0 && (
                          <p className="text-sm text-gray-700">
                            <strong>Active Requests:</strong> {city.requests_count}
                          </p>
                        )}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
            {validCityStats.length === 0 && (
              <div className="p-8 text-center text-gray-600">
                <p>No city data available to display on the map.</p>
              </div>
            )}
          </div>
        )}

        {/* Top 10 Cities List */}
        {!loading && !error && topCities.length > 0 && (
          <div data-testid="top-cities-list" className="mt-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Top 10 Cities by Donor Count</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {topCities.map((city, index) => (
                <Card key={city.id || `${city.city}-${city.country}`} className="border shadow-sm">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-red-700">#{index + 1}</span>
                          <h3 data-testid={`city-item-${index}`} className="font-semibold text-gray-900">
                            {city.city}, {city.country}
                          </h3>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">
                          {city.donor_count || 0} {city.donor_count === 1 ? 'donor' : 'donors'}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default GlobalMap;
